<template>
    <BaseModal :title="$t('title')" v-on="$listeners">
        <div class="l-stack l-gap-2 dashboard-reporting-modal">
            <BaseMultiselect
                v-model="entry"
                :placeholder="$t('entry')"
                :allow-empty="false"
                :options="entries"
                track-by="id"
                label="name"
                block
            />

            <BaseInput
                v-model="name"
                :disabled="isSubmitting"
                :placeholder="$t('name')"
                block
            />

            <BaseCard padded class="l-stack l-gap-2">
                <BaseMultiselect
                    v-model="emailType"
                    :options="emailTypeOptions"
                    :custom-label="option => $t(`emailType${option}`)"
                    :disabled="isSubmitting"
                />

                <label v-if="emailType === 'CUSTOM'">
                    <p class="form-label">
                        {{ $t('recipient') }}
                    </p>

                    <p v-if="$v.email.$error" class="form-error">
                        {{ $t('emailInvalid') }}
                    </p>

                    <BaseInput
                        v-model="$v.email.$model"
                        :disabled="isSubmitting"
                        :placeholder="$t('email')"
                    />
                </label>

                <InfoBox v-else-if="emailType === 'PRIMARY'">
                    {{ $t('emailPrimaryHelp') }}

                    <strong>
                        {{ userEmailListFormatted || $t('emailPrimarEmpty') }}
                    </strong>
                </InfoBox>
            </BaseCard>

            <BaseCard v-if="hasDaterangeFilter" padded class="l-stack l-gap-2">
                <div>
                    <p class="form-label">{{ $t('from') }}</p>

                    <div class="l-inline l-gap-2">
                        <DateInput
                            v-model="dateFrom"
                            placeholder="DD.MM.YYYY"
                        />

                        <TimeInput
                            v-model="timeFrom"
                            placeholder="HH:mm"
                            full
                        />
                    </div>
                </div>

                <div>
                    <p class="form-label">{{ $t('to') }}</p>

                    <div class="l-inline l-gap-2">
                        <DateInput v-model="dateTo" placeholder="DD.MM.YYYY" />

                        <TimeInput v-model="timeTo" placeholder="HH:mm" full />
                    </div>
                </div>
            </BaseCard>

            <BaseCard
                v-if="hasAssetsFilter || hasLocationsFilter"
                padded
                class="l-stack l-gap-2"
            >
                <div v-if="hasLocationsFilter">
                    <p class="form-label">
                        {{ $t('locations') }}
                    </p>

                    <BaseMultiselect
                        v-model="locations"
                        :options="locationsSortedByName"
                        track-by="id"
                        :custom-label="option => option.name"
                        :placeholder="$t('locations')"
                        multiple
                    />
                </div>

                <div v-if="hasAssetsFilter">
                    <p class="form-label">{{ $t('shared.assets') }}</p>

                    <div class="l-stack l-gap-1">
                        <BaseMultiselect
                            v-model="assetSelector"
                            :options="assetSelectorOptions"
                            :custom-label="
                                option =>
                                    $t(`assetSelector-${option}`, [
                                        $t('shared.assets'),
                                    ])
                            "
                            :placeholder="$t('assetSelectorPlaceholder')"
                            @input="assetSelectorValue = []"
                        />

                        <BaseMultiselect
                            v-if="assetSelector && assetSelector !== 'all'"
                            v-model="assetSelectorValue"
                            :options="assetSelectorValueOptions"
                            track-by="id"
                            :custom-label="getAssetSelectorValueOptionLabel"
                            :placeholder="
                                $t(`assetSelector-${assetSelector}`, [
                                    $t('shared.assets'),
                                ])
                            "
                            multiple
                        />
                    </div>
                </div>
            </BaseCard>

            <BaseCard
                v-if="needsAttachment"
                padded
                class="dashboard-reporting-modal__attachment"
            >
                <p class="form-label">
                    {{ $t('attachment') }}
                    <span class="required-marker" aria-hidden="true">*</span>
                </p>

                <IconButton
                    :disabled="isSubmitting"
                    @click="$refs.attachmentInput.click()"
                >
                    <AttachmentIcon />

                    <input
                        ref="attachmentInput"
                        type="file"
                        :disabled="isSubmitting"
                        required
                        aria-required="true"
                        @change="handleAttachmentChange"
                    />
                </IconButton>

                <div
                    v-if="attachment"
                    class="dashboard-reporting-modal__attachment-file l-inline l-center-v l-gap-1"
                >
                    <FileIcon width="1em" height="1em" />

                    <span class="t-truncated">{{ attachment.name }}</span>

                    <IconButton
                        :disabled="isSubmitting"
                        @click="removeAttachment"
                    >
                        <RemoveIcon width="1em" height="1em" />
                    </IconButton>
                </div>
            </BaseCard>

            <label
                v-if="emailType !== 'NONE'"
                class="l-inline l-center-v l-gap-1"
            >
                <ToggleButton v-model="isStore" redesigned />

                <span class="t-small">{{ $t('storeLabel') }}</span>
            </label>
        </div>

        <template #footer>
            <div class="l-inline l-justify-end">
                <BaseButton
                    size="small"
                    :disabled="isDisabled"
                    :is-loading="isLoading || isSubmitting"
                    @click="handleSubmit"
                >
                    {{ $t('submit') }}
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment-timezone'
import { email } from 'vuelidate/lib/validators'

import { httpHelper } from '@/utils'
import AttachmentIcon from '@/components/icons/AttachmentIcon'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseCard from '@/components/redesigned/BaseCard'
import BaseInput from '@/components/redesigned/BaseInput'
import BaseModal from '@/components/redesigned/BaseModal'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import DateInput from '@/components/redesigned/DateInput'
import FileIcon from '@/components/icons/FileIcon'
import IconButton from '@/components/IconButton'
import InfoBox from '@/components/InfoBox'
import RemoveIcon from '@/components/icons/RemoveIcon'
import TimeInput from '@/components/redesigned/TimeInput'
import ToggleButton from '@/components/ToggleButton'

export default {
    name: 'DashboardReportingModal',
    components: {
        AttachmentIcon,
        BaseButton,
        BaseCard,
        BaseInput,
        BaseModal,
        BaseMultiselect,
        DateInput,
        FileIcon,
        IconButton,
        InfoBox,
        RemoveIcon,
        TimeInput,
        ToggleButton,
    },
    props: {
        onSubmit: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            assetSelector: null,
            assetSelectorOptions: ['all', 'types', 'assets'],
            assetSelectorValue: [],
            attachment: null,
            dateFrom: null,
            dateTo: null,
            email: '',
            emailType: 'NONE',
            emailTypeOptions: ['NONE', 'PRIMARY', 'CUSTOM'],
            entries: [],
            entry: [],
            isFetching: false,
            isStore: false,
            isSubmitting: false,
            locations: [],
            name: '',
            timeFrom: '',
            timeTo: '',
        }
    },
    computed: {
        ...mapState('auth', ['userInfo']),
        ...mapState('tracker', ['assetTypes', 'trackers']),
        ...mapGetters('tracker', ['assetsInLocation']),
        ...mapGetters('location', ['locationsSortedByName']),
        assetSelectorValueOptions() {
            switch (this.assetSelector) {
                case 'assets':
                    return this.trackers
                case 'types':
                    return this.assetTypes
                default:
                    return []
            }
        },
        hasAssetsFilter() {
            return this.entry?.assets_filter
        },
        hasDaterangeFilter() {
            return this.entry?.daterange_filter
        },
        hasLocationsFilter() {
            return this.entry?.locations_filter
        },
        needsAttachment() {
            return this.entry?.needs_attachment
        },
        isDisabled() {
            const hasCustomEmail =
                this.emailType === 'CUSTOM' &&
                this.email &&
                !this.$v.email.$error
            const hasPrimaryEmail =
                this.emailType === 'PRIMARY' &&
                this.userInfo.alertsettings.email_recipients.length

            return !(
                (this.emailType === 'NONE' ||
                    hasCustomEmail ||
                    hasPrimaryEmail) &&
                this.entry &&
                this.name &&
                (!this.needsAttachment || this.attachment) &&
                (!this.hasDaterangeFilter || (this.dateFrom && this.dateTo))
            )
        },
        isLoading() {
            return this.isFetching || this.isSubmitting
        },
        userEmailListFormatted() {
            return this.userInfo.alertsettings.email_recipients.join('; ')
        },
    },
    watch: {
        entry() {
            this.dateFrom = null
            this.dateTo = null
            this.attachment = null

            if (this.$refs.attachmentInput) {
                this.$refs.attachmentInput.value = ''
            }

            if (this.entry) {
                this.name = `${this.entry.name} (${moment().format(
                    'DD.MM.YYYY HH:mm'
                )})`
            }
        },
        hasAssetsFilter() {
            this.assetSelector = null
            this.assetSelectorValue = []
        },
        hasDaterangeFilter() {
            this.timeFrom = this.hasDaterangeFilter ? '00:00' : ''
            this.timeTo = this.hasDaterangeFilter ? '23:59' : ''
        },
        hasLocationsFilter() {
            this.locations = []
        },
    },
    validations() {
        return {
            email: {
                email,
            },
        }
    },
    mounted() {
        this.fetchEntries()
    },
    methods: {
        async fetchEntries() {
            this.isFetching = true
            this.entries = []
            const { data } = await httpHelper.getRecursively('/report-queries/')
            if (data.results?.length) {
                this.entries = data.results
            }
            this.isFetching = false
        },
        formatDate(date, time) {
            return moment(date)
                .set({
                    hour: time.split(':')[0],
                    minute: time.split(':')[1],
                    second: 0,
                    millisecond: 0,
                })
                .format('YYYY-MM-DDTHH:mm:ssZ')
        },
        getAssetSelectorValueOptionLabel(option) {
            return this.assetSelector === 'assets'
                ? `${option.asset_details.name} (${option.identifier})`
                : option.name
        },
        handleAttachmentChange(event) {
            this.attachment = event.target.files[0] || null
        },
        removeAttachment() {
            this.attachment = null

            if (this.$refs.attachmentInput) {
                this.$refs.attachmentInput.value = ''
            }
        },
        createPayload() {
            return {
                name: this.name,
                store: this.emailType === 'NONE' ? true : this.isStore,
                email:
                    this.emailType === 'PRIMARY'
                        ? this.userInfo.alertsettings.email_recipients
                        : this.emailType === 'CUSTOM'
                        ? [this.email]
                        : [],
                ...(this.hasAssetsFilter && {
                    assets: (this.assetSelector === 'all'
                        ? this.trackers
                        : this.assetSelector === 'assets'
                        ? this.assetSelectorValue
                        : this.assetSelector === 'types'
                        ? this.trackers.filter(tracker =>
                              this.assetSelectorValue.find(
                                  ({ id }) =>
                                      id === tracker.asset_details.asset_type
                              )
                          )
                        : []
                    ).map(({ asset }) => asset),
                }),
                ...(this.hasDaterangeFilter && {
                    date_from: this.formatDate(this.dateFrom, this.timeFrom),
                    date_to: this.formatDate(this.dateTo, this.timeTo),
                }),
                ...(this.hasLocationsFilter && {
                    locations: this.locations.map(({ id }) => id),
                }),
            }
        },
        createMultipartPayload(payload) {
            const formData = new FormData()

            Object.entries(payload).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => formData.append(key, item))
                } else if (value !== null && value !== undefined) {
                    formData.append(key, value)
                }
            })

            formData.append('attachment', this.attachment, this.attachment.name)

            return formData
        },
        async handleSubmit() {
            if (this.isDisabled) {
                return
            }

            try {
                this.isSubmitting = true
                const payload = this.createPayload()
                const requestPayload = this.needsAttachment
                    ? this.createMultipartPayload(payload)
                    : payload

                await httpHelper.post(this.entry.generate_url, requestPayload)

                this.onSubmit?.()
                this.$emit('close')
                this.$notify({
                    title: this.$t('successNotificationTitle'),
                    text: this.$t('successNotificationText'),
                })
            } finally {
                this.isSubmitting = false
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "assetSelector-all": "All {0}",
        "assetSelector-assets": "Individual {0}",
        "assetSelector-types": "{0} types",
        "assetSelectorPlaceholder": "Selector",
        "attachment": "Attachment",
        "email": "E-Mail",
        "emailInvalid": "Invalid E-Mail",
        "emailPrimaryEmpty": "(empty)",
        "emailPrimaryHelp": "Goes to all email addresses from the owner's user profile.",
        "emailTypeNONE": "Don't send E-Mail",
        "emailTypeCUSTOM": "E-mail to different address",
        "emailTypePRIMARY": "E-Mail to me",
        "entry": "Entry",
        "from": "From",
        "locations": "Locations",
        "name": "Name",
        "recipient": "Recipient",
        "storeLabel": "Save report data",
        "submit": "Generate",
        "successNotificationText": "The report is now being generated and will be ready soon.",
        "successNotificationTitle": "Successfully!",
        "title": "Create a new report",
        "to": "To"
    },
    "de": {
        "assetSelector-all": "Alle {0}",
        "assetSelector-assets": "Individuelle {0}",
        "assetSelector-types": "{0} Typ",
        "assetSelectorPlaceholder": "Selektor",
        "attachment": "Anhang",
        "email": "E-Mail",
        "emailInvalid": "Ungültige E-Mail",
        "emailPrimarEmpty": "(leer)",
        "emailPrimaryHelp": "Geht an alle E-Mail Adressen aus Ihrem Benutzerprofil:",
        "emailTypeNONE": "Keine E-Mail senden",
        "emailTypeCUSTOM": "E-Mail an abweichende Adresse",
        "emailTypePRIMARY": "E-Mail an mich",
        "entry": "Eintrag",
        "from": "Von",
        "locations": "Standorte",
        "name": "Name",
        "recipient": "Empfänger",
        "storeLabel": "Reportsdaten speichern",
        "submit": "Generieren",
        "successNotificationText": "Der Report wird jetzt erstellt und wird bald verfügbar sein.",
        "successNotificationTitle": "Erfolgreich!",
        "title": "Einen neuen Report erstellen",
        "to": "Bis"
    },
    "fr": {
        "assetSelector-all": "Tout {0}",
        "assetSelector-assets": "Individuel {0}",
        "assetSelector-types": "{0} types",
        "assetSelectorPlaceholder": "Sélecteur",
        "attachment": "Piece jointe",
        "email": "E-mail",
        "emailInvalid": "E-mail invalide",
        "emailPrimarEmpty": "(vide)",
        "emailPrimaryHelp": "Il est envoyé à toutes les adresses e-mail du profil utilisateur du propriétaire",
        "emailTypeNONE": "Ne pas envoyer d'e-mail",
        "emailTypeCUSTOM": "E-mail à une adresse différente",
        "emailTypePRIMARY": "E-mail à moi-même",
        "entry": "Entrée",
        "from": "De",
        "locations": "Emplacements",
        "name": "Nom",
        "recipient": "Destinataire",
        "storeLabel": "Sauvegarder les données du rapport",
        "submit": "Générer",
        "successNotificationText": "Le rapport est en cours de génération et sera bientôt prêt.",
        "successNotificationTitle": "Succès !",
        "title": "Créer un nouveau rapport",
        "to": "À"
    },
    "it": {
        "assetSelector-all": "Tutti {0}",
        "assetSelector-assets": "{0} individuale",
        "assetSelector-types": "{0} Typo",
        "assetSelectorPlaceholder": "Selettore",
        "attachment": "Allegato",
        "email": "E-Mail",
        "emailInvalid": "E-Mail non valido",
        "emailPrimaryEmpty": "(vuoto)",
        "emailPrimaryHelp": "Va a tutti gli indirizzi e-mail dal profilo utente del proprietario.",
        "emailTypeNONE": "Non inviare e-mail",
        "emailTypeCUSTOM": "E-mail a un indirizzo diverso",
        "emailTypePRIMARY": "E-mail a me",
        "entry": "Entrata",
        "from": "Da",
        "locations": "Locatione",
        "name": "Nome",
        "recipient": "Destinatario",
        "storeLabel": "Salvare i dati del rapporto",
        "submit": "Generare",
        "successNotificationText": "Il rapporto è in fase di generazione e sarà pronto a breve.",
        "successNotificationTitle": "Successo!",
        "title": "Creare un nuovo rapporto",
        "to": "A"
    }
}
</i18n>

<style lang="scss" scoped>
.dashboard-reporting-modal {
    .information-box {
        margin-bottom: 0;
    }

    &__attachment {
        .icon-button {
            margin: 0.5rem;

            input {
                display: none;
            }
        }

        svg {
            min-width: 1em;
        }
    }

    &__attachment-file {
        padding-left: 0.5rem;
    }

    .required-marker {
        color: $color-error;
    }
}
</style>
